import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, User, ArrowRight, ArrowLeft, AlertCircle, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTenant, tenantBranding } from "@/hooks/useTenant";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SelectDoctor = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant } = useTenant();
  const brand = tenantBranding(tenant);
  // When no tenant is resolved (root Thales flow), fall back to the platform
  // logo instead of showing a client's brand. The tenant branding hex color is
  // still used so buttons stay consistent within a single visit.
  const showThalesFallback = !tenant;
  const [step, setStep] = useState('identify');
  const [dni, setDni] = useState('');
  const [loading, setLoading] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [prefillToken, setPrefillToken] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState(null);

  // Auto-load doctors if token is in URL (from magic link email)
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      loadDoctorsWithToken(token);
    }
  }, [searchParams]);

  const loadDoctorsWithToken = async (token) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/patients/my-doctors?token=${token}`);
      const data = await response.json();
      if (response.ok && data.doctors && data.doctors.length > 0) {
        setDoctors(data.doctors);
        if (data.prefill_token) {
          setPrefillToken(data.prefill_token);
        }
        setStep('select');
      } else if (response.status === 401) {
        toast.error(data.detail || "Enlace invalido o expirado");
        setStep('identify');
      } else {
        setStep('no-doctors');
      }
    } catch (error) {
      toast.error("Error al conectar con el servidor");
      setStep('identify');
    } finally {
      setLoading(false);
    }
  };

  const handleSendLink = async () => {
    if (!dni.trim() || dni.trim().length < 7) {
      toast.error("Ingresa un DNI valido");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/patients/send-access-link?dni=${dni.trim()}`, {
        method: 'POST'
      });
      const data = await response.json();
      if (response.ok) {
        setMaskedEmail(data.masked_email || '');
        setStep('link-sent');
      } else if (response.status === 404) {
        setStep('no-doctors');
      } else {
        toast.error(data.detail || "Error al enviar el enlace");
      }
    } catch (error) {
      toast.error("Error al conectar con el servidor");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDoctor = (doctor, institution) => {
    const target = `/${institution.slug}/${doctor.slug}`;
    if (prefillToken) {
      navigate(`${target}?prefill_token=${encodeURIComponent(prefillToken)}`);
    } else {
      navigate(target);
    }
  };

  const handleDoctorCardClick = (doctor) => {
    const insts = doctor.institutions || [];
    if (insts.length === 0) {
      toast.info("Este profesional no tiene enlace configurado aun");
      return;
    }
    if (insts.length === 1) {
      handleSelectDoctor(doctor, insts[0]);
      return;
    }
    setSelectedDoctor(doctor);
    setStep('select-tenant');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')} data-testid="select-doctor-nav-logo">
              {showThalesFallback ? (
                <>
                  <img src="/logo-thales.png" alt="Thales Health" className="h-8 sm:h-9 w-auto" />
                  <span
                    className="font-semibold text-base sm:text-lg tracking-tight text-slate-900"
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    Thales Health
                  </span>
                </>
              ) : brand.logoUrl ? (
                <img src={brand.logoUrl} alt={brand.name} className="h-10 sm:h-12 object-contain" />
              ) : (
                <span
                  className="font-semibold text-base sm:text-lg tracking-tight"
                  style={{ color: brand.primaryColor, fontFamily: "'Manrope', sans-serif" }}
                >
                  {brand.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-lg mx-auto px-4 py-12">
        {/* Loading state */}
        {loading && step === 'identify' && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            <p className="text-slate-500">Verificando...</p>
          </div>
        )}

        {/* Step: Identify by DNI */}
        {step === 'identify' && !loading && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-8">
              <div
                className="h-14 w-14 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: `${brand.primaryColor}1A` }}
              >
                <Search className="h-7 w-7" style={{ color: brand.primaryColor }} />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Iniciar Consulta</h1>
              <p className="text-slate-500">Ingresa tu DNI para acceder a tus profesionales</p>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="text-sm text-slate-700">DNI (sin puntos)</Label>
                <Input
                  type="text"
                  placeholder="Ej: 30123456"
                  value={dni}
                  onChange={(e) => setDni(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendLink()}
                  className="h-12 mt-1"
                  data-testid="patient-dni-input"
                />
              </div>
              <Button
                onClick={handleSendLink}
                disabled={loading || !dni.trim()}
                className="w-full h-12 text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: brand.primaryColor }}
                data-testid="send-access-link-btn"
              >
                {loading ? "Enviando..." : "Enviar enlace de acceso"}
                <Mail className="ml-2 h-4 w-4" />
              </Button>
            </div>

            <p className="text-xs text-slate-400 text-center mt-4">
              Te enviaremos un enlace seguro a tu email registrado
            </p>

            <div className="mt-6 pt-6 border-t border-slate-100">
              <Button
                variant="ghost"
                className="w-full text-slate-500"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver al inicio
              </Button>
            </div>
          </div>
        )}

        {/* Step: Link sent - check email */}
        {step === 'link-sent' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="h-14 w-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-7 w-7 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Revisa tu email</h1>
              <p className="text-slate-500">
                Enviamos un enlace de acceso a <strong>{maskedEmail}</strong>
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-blue-800">
                Abre tu correo y hace clic en <strong>"Ver mis profesionales"</strong> para continuar. El enlace expira en 1 hora.
              </p>
            </div>

            <div className="p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2.5 mb-6" data-testid="spam-alert">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-900">
                <strong>¿No te llegó?</strong> Revisá tu carpeta de <strong>spam o correo no deseado</strong>.
              </p>
            </div>

            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={() => { setStep('identify'); setDni(''); }}
              >
                Usar otro DNI
              </Button>
              <Button
                variant="ghost"
                className="w-full text-slate-500"
                onClick={() => navigate('/')}
              >
                Volver al inicio
              </Button>
            </div>
          </div>
        )}

        {/* Step: Select doctor (after magic link verification) */}
        {step === 'select' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Tus profesionales</h1>
              <p className="text-slate-500">Selecciona a quien queres consultar</p>
            </div>

            <div className="space-y-3">
              {doctors.map((doctor) => {
                const insts = doctor.institutions || [];
                const hasMultiple = insts.length > 1;
                // Prefer the doctor's own institution color so a Fleni doctor
                // stays magenta/navy even when the visitor is on the neutral
                // Thales root. Falls back to the visitor's tenant color, then
                // to Fleni navy as final safety net.
                const primaryColor = insts[0]?.branding?.primary_color || brand.primaryColor;
                return (
                  <button
                    key={doctor.id}
                    onClick={() => handleDoctorCardClick(doctor)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all text-left mb-2 ${insts.length === 0 ? 'opacity-60' : ''}`}
                    style={{ borderColor: undefined }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = primaryColor; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
                    data-testid={`select-doctor-${doctor.slug}`}
                  >
                    <div className="h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: insts.length === 0 ? '#94a3b8' : primaryColor }}>
                      <User className="h-6 w-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 truncate">{doctor.name}</p>
                      {doctor.specialty && (
                        <p className="text-sm text-slate-500">{doctor.specialty}</p>
                      )}
                      {hasMultiple && (
                        <p className="text-xs text-slate-400 mt-0.5">
                          {insts.length} puntos de atencion
                        </p>
                      )}
                      {!hasMultiple && insts.length === 1 && (
                        <p className="text-xs text-slate-400 mt-0.5">{insts[0].name}</p>
                      )}
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100">
              <Button
                variant="ghost"
                className="w-full text-slate-500"
                onClick={() => navigate('/')}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver al inicio
              </Button>
            </div>
          </div>
        )}

        {/* Step: Select tenant / point of care for the chosen doctor */}
        {step === 'select-tenant' && selectedDoctor && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Donde queres consultar con {selectedDoctor.name}?
              </h1>
              {selectedDoctor.specialty && (
                <p className="text-slate-500">{selectedDoctor.specialty}</p>
              )}
            </div>

            <div className="space-y-3">
              {(selectedDoctor.institutions || []).map((inst) => (
                <button
                  key={`${selectedDoctor.id}-${inst.id}`}
                  onClick={() => handleSelectDoctor(selectedDoctor, inst)}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-slate-200 hover:bg-slate-50 transition-all text-left mb-2"
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = inst.branding?.primary_color || brand.primaryColor; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
                  data-testid={`select-tenant-${inst.slug}`}
                >
                  <div className="h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: inst.branding?.primary_color || brand.primaryColor }}>
                    <User className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{inst.name}</p>
                    {inst.last_consult_at && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        Ultima consulta: {new Date(inst.last_consult_at).toLocaleDateString('es-AR')}
                      </p>
                    )}
                  </div>
                  <ArrowRight className="h-5 w-5 text-slate-300 flex-shrink-0" />
                </button>
              ))}
            </div>

            <div className="mt-6 pt-6 border-t border-slate-100 space-y-2">
              <Button
                variant="ghost"
                className="w-full text-slate-500"
                onClick={() => { setSelectedDoctor(null); setStep('select'); }}
                data-testid="back-to-doctors-btn"
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Volver a mis profesionales
              </Button>
            </div>
          </div>
        )}

        {/* Step: No doctors found */}
        {step === 'no-doctors' && (
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="h-14 w-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="h-7 w-7 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Sin profesionales asociados</h1>
              <p className="text-slate-500 leading-relaxed">
                Todavia no tenes medicos vinculados a tu DNI. Para iniciar tu primera consulta, necesitas el enlace personalizado que te proporciono tu profesional.
              </p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-slate-600">
                <strong>¿Como obtengo el enlace?</strong><br/>
                Solicita a tu medico el link de consulta. Lo podes recibir por WhatsApp, email o en persona con un codigo QR.
              </p>
            </div>

            <div className="space-y-3">
              <Button
                variant="ghost"
                className="w-full text-slate-500"
                onClick={() => { setStep('identify'); setDni(''); }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Intentar con otro DNI
              </Button>
              <Button
                variant="ghost"
                className="w-full text-slate-500"
                onClick={() => navigate('/')}
              >
                Volver al inicio
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectDoctor;
